// Shared image-rotation controller for product-card media, used by both
// snippets/collection-product-card.liquid (/collections/* pages, incl. the
// Bundles collection - a bundle IS a normal product with its own top-level
// images, so `product.images` there is already the bundle product's own
// media, never a component product's) and sections/product-carousel.liquid
// (homepage "Our Fabulous Products"). Any future card that reuses the same
// [data-card-media] contract (e.g. search/recommendations) inherits this
// automatically - loaded once, globally, in layout/theme.liquid.
//
// Each card gets its own independent controller (attached to the DOM node as
// `mediaRoot._veloCardMedia`) and its own image data - no cross-card state.
// A single shared IntersectionObserver gates play/pause so only on-screen
// cards ever run a timer (no per-card timer accumulation for off-screen
// cards). Pointer Events power touch/mouse swipe without opening the
// product-page link on a real drag; a plain tap still navigates normally.
//
// Variant-featured-media override: assets/collection-product-card.js calls
// window.Velo.showCardVariantImage / hideCardVariantImage (never touch
// rotation DOM directly) so there is one source of truth per card.
//
// data-swipe="false" opts a card out of the pointer-drag-to-change-image
// gesture while keeping autoplay - used by sections/product-carousel.liquid,
// whose cards sit inside a native horizontally-scrollable snap track (swipe
// between PRODUCT CARDS on mobile). Without the opt-out, this controller's
// own preventDefault()-on-horizontal-intent would fight that native scroll.
(function () {
  var DRAG_THRESHOLD = 40;
  var DIRECTION_LOCK_THRESHOLD = 10;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var observer = null;
  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var controller = entry.target._veloCardMedia;
        if (!controller) return;
        controller.setVisible(entry.isIntersecting);
      });
    }, { threshold: 0.25 });
    return observer;
  }

  function initCardMedia(root) {
    if (root.dataset.cardMediaInitialized === 'true') return;

    var images = Array.prototype.slice.call(root.querySelectorAll('[data-card-media-image]'));
    var variantImg = root.querySelector('[data-card-variant-image]');
    if (!images.length) return;

    root.dataset.cardMediaInitialized = 'true';

    var current = 0;
    var speed = parseInt(root.dataset.autoplaySpeed, 10) || 1800;
    var autoplayAllowed = root.dataset.autoplay !== 'false' && images.length > 1;
    var timer = null;
    var visible = false;
    var dragging = false;
    var variantOverride = false;

    function goTo(index) {
      current = (index + images.length) % images.length;
      images.forEach(function (img) {
        var active = parseInt(img.dataset.index, 10) === current;
        img.classList.toggle('opacity-0', !active);
      });
    }

    function canAutoplay() {
      return autoplayAllowed
        && !prefersReducedMotion()
        && !document.hidden
        && visible
        && !dragging
        && !variantOverride;
    }

    function stopTimer() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function startTimer() {
      stopTimer();
      if (!canAutoplay()) return;
      timer = setInterval(function () {
        if (!canAutoplay()) return;
        goTo(current + 1);
      }, speed);
    }

    function resetTimer() {
      stopTimer();
      startTimer();
    }

    function manualGoTo(index) {
      goTo(index);
      resetTimer();
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        stopTimer();
      } else {
        resetTimer();
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Pointer Events swipe (touch + mouse), scoped to this card's media only.
    // Suppresses the trailing click on the wrapping <a> after a real drag so
    // swiping never accidentally opens the product page; a plain tap still
    // navigates normally.
    var link = root.closest('a');
    var startX = 0;
    var startY = 0;
    var pointerId = null;
    var directionLocked = null;
    var suppressNextClick = false;
    var swipeEnabled = root.dataset.swipe !== 'false';

    if (images.length > 1 && swipeEnabled) {
      root.addEventListener('pointerdown', function (e) {
        startX = e.clientX;
        startY = e.clientY;
        pointerId = e.pointerId;
        directionLocked = null;
        dragging = true;
        stopTimer();
      });

      root.addEventListener('pointermove', function (e) {
        if (pointerId === null || e.pointerId !== pointerId) return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;

        if (!directionLocked && (Math.abs(dx) > DIRECTION_LOCK_THRESHOLD || Math.abs(dy) > DIRECTION_LOCK_THRESHOLD)) {
          directionLocked = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
          if (directionLocked === 'horizontal' && root.setPointerCapture) {
            try { root.setPointerCapture(pointerId); } catch (err) { /* noop */ }
          }
        }
        if (directionLocked === 'horizontal') {
          e.preventDefault();
        }
      });

      function endDrag(e) {
        if (pointerId === null) {
          dragging = false;
          return;
        }
        if (directionLocked === 'horizontal' && e) {
          var dx = e.clientX - startX;
          if (Math.abs(dx) >= DRAG_THRESHOLD) {
            suppressNextClick = true;
            manualGoTo(dx < 0 ? current + 1 : current - 1);
          } else {
            resetTimer();
          }
        } else {
          resetTimer();
        }
        pointerId = null;
        directionLocked = null;
        dragging = false;
      }

      root.addEventListener('pointerup', endDrag);
      root.addEventListener('pointercancel', endDrag);

      root.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });

      if (link) {
        link.addEventListener('click', function (e) {
          if (suppressNextClick) {
            e.preventDefault();
            e.stopPropagation();
            suppressNextClick = false;
          }
        }, true);
      }
    }

    root._veloCardMedia = {
      setVisible: function (isVisible) {
        visible = isVisible;
        resetTimer();
      },
      showVariantImage: function (src) {
        variantOverride = true;
        stopTimer();
        if (variantImg && src) {
          variantImg.setAttribute('src', src);
          variantImg.classList.remove('opacity-0', 'pointer-events-none');
        }
      },
      hideVariantImage: function () {
        variantOverride = false;
        if (variantImg) {
          variantImg.classList.add('opacity-0', 'pointer-events-none');
        }
        resetTimer();
      },
      stop: function () {
        stopTimer();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        getObserver().unobserve(root);
      }
    };

    goTo(0);
    getObserver().observe(root);
  }

  window.Velo = window.Velo || {};
  window.Velo.showCardVariantImage = function (mediaRoot, src) {
    if (mediaRoot && mediaRoot._veloCardMedia) mediaRoot._veloCardMedia.showVariantImage(src);
  };
  window.Velo.hideCardVariantImage = function (mediaRoot) {
    if (mediaRoot && mediaRoot._veloCardMedia) mediaRoot._veloCardMedia.hideVariantImage();
  };

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-card-media]:not([data-card-media-initialized="true"])').forEach(initCardMedia);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });
  if (document.readyState !== 'loading') {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-card-media]').forEach(function (root) {
      if (root._veloCardMedia) root._veloCardMedia.stop();
      root.dataset.cardMediaInitialized = 'false';
    });
  });

  document.addEventListener('shopify:section:select', function (e) {
    e.target.querySelectorAll('[data-card-media]').forEach(function (root) {
      if (root._veloCardMedia) root._veloCardMedia.setVisible(true);
    });
  });
})();
